import React, { Fragment, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import HeaderV2 from "../../components/headerV2/HeaderV2";
import FooterV2 from "../../components/footerV2/FooterV2";
import AboutHero from "../../components/aboutHero/AboutHero";
import useScrollReveal from "../../hooks/useScrollReveal";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import "../AboutUsPage/aboutV2.css";

/* Photo supplied in the Sep 2026 website review (lives in /public). */
const SuccessStoriesImg = process.env.PUBLIC_URL + "/images/about-v2/success-stories.jpg";

const STORIES = [
  {
    title: "A Life Saved Through a QR Code",
    text: "In June 2026, a patient arrived at Ram Krishna Mission Hospital, Varanasi, in critical need of blood. With no donor available through conventional channels, the patient's family noticed a Life Saver Army QR code sticker at the hospital entrance - placed there by Jainam Jivika Foundation's outreach team. They scanned it, registered the emergency, and within hours a verified donor arrived. This was the first confirmed life saved through the Life Saver Army platform - a milestone that validated every camp, every registration drive, and every sticker placed.",
  },
  {
    title: "A University Becomes a Partner in Saving Lives",
    text: "MGKVP University in Varanasi hosted a Life Saver Army orientation and blood donation session organised by Jainam Jivika Foundation. Students who attended did not just register - they became ambassadors, spreading the word within their hostels, departments, and home communities. BHU's NSS unit and Sir Sunderlal Hospital followed, embedding Life Saver Army into campus and institutional life across the city.",
  },
  {
    title: "Platelet Donors Step Up for Cancer Patients",
    text: "When a leukaemia patient at a Varanasi hospital required multiple platelet donations over a fortnight, Life Saver Army mobilised a group of registered platelet donors who responded within 24 hours across multiple sessions. The patient completed treatment, and three of the donors have since become regular contributors to the network.",
  },
  {
    title: "A Volunteer Network That Crosses State Lines",
    text: "When a rare blood type was urgently needed for a patient in Bihar, Life Saver Army's cross-state donor network was activated. A registered donor from Patna answered the call, travelling to the hospital in time. This story highlighted the power of building a nationwide database of committed voluntary donors - and the difference it makes when geography is no longer a barrier.",
  },
  {
    title: "Students Become Ambassadors for Blood Donation",
    text: "Following a Life Saver Army awareness session at a Varanasi engineering college, a group of 40 students launched their own peer-to-peer donor registration drive on campus. Within two weeks, they had recruited 120 new donors and partnered with their college NSS unit to make blood donation part of the institution's annual calendar.",
  },
];

const SuccessStories = ({ path }) => {
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Success Stories" });
  const [schemadata, setSchemadata] = useState({});

  const rootRef = useRef(null);
  useScrollReveal(rootRef);

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];

    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      tempMetadata = {
        title: "JJFIndia | Success Stories",
        description: "JJFIndia Impact and Stories page",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/impact/success-stories",
        openGraph: {
          title: "JJFIndia | Success Stories",
          description: "JJFIndia Impact and Stories page",
          url: "https://jjfindia.com/impact/success-stories",
        },
        twitter: {
          card: "summary_large_image",
          title: "JJFIndia | Success Stories",
          description: "JJFIndia Impact and Stories page",
        },
      };
      setMetadata(tempMetadata);
    }

    if (tempSchemadata) {
      setSchemadata(tempSchemadata);
    }
  }, [path]);

  const scrollTop = () => window.scrollTo(10, 0);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <div className="jjfv2 about-live" ref={rootRef}>
        <HeaderV2 />

        <main className="page_content">
          {/* ---------- Page hero ---------- */}
          <AboutHero
            eyebrow="Stories of impact"
            title="Success Stories"
            crumbs={[{ label: "Home", to: "/" }, { label: "Impacts & Stories" }]}
          />

          {/* ---------- Intro ---------- */}
          <section className="section about" id="stories-intro" style={{ paddingTop: "36px", paddingBottom: "36px" }}>
            <div className="wrap split" style={{ position: "relative", zIndex: 1 }}>
              <div className="about-media reveal">
                <span className="leaf-frame" aria-hidden="true"></span>
                <div className="ph">
                  <img src={SuccessStoriesImg} alt="Jainam Jivika Foundation founders honoured at a Life Saver Army blood donation camp" loading="lazy" />
                </div>
              </div>
              <div className="about-copy reveal d1">
                <span className="eyebrow">Real cases, real outcomes</span>
                <h2>Every Story Is a Person</h2>
                <p className="lead">
                  A child who received platelets when it mattered most. An emergency case matched in
                  the middle of the night. These are the stories of Life Saver Army at work, told by
                  the families and volunteers who lived them.
                </p>
              </div>
            </div>
          </section>

          {/* ---------- Stories ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Inspiring Stories of Change</span>
                <h2>Real Stories of Hope, Change and Impact</h2>
              </div>
              <div className="stories">
                {STORIES.map((s, i) => (
                  <article className={"story reveal" + (i % 2 ? " d1" : "")} key={i}>
                    <span className="story__num">{String(i + 1).padStart(2, "0")}</span>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- Share your story ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Share Your Story</span>
                <h2>Your Story Matters</h2>
                <p className="lead">
                  Has Jainam Jivika Foundation or Life Saver Army made a difference in your life? We
                  would love to hear from you. Share your experience and inspire others to join this
                  journey towards a brighter, kinder future.
                </p>
                <Link to="/contact-us" onClick={scrollTop} className="btn btn--green" style={{ marginTop: "1.7rem" }}>
                  Share Your Story
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <FooterV2 />
      </div>
    </Fragment>
  );
};

export default SuccessStories;
